/**
 * Компонент "Бейдж кластера на услуге" — отображает кластеры, в которых находится услуга
 * Дата: 07.02.2026 - PLAN_06.02.2026_SERVICE_CLUSTERS.md (Итерация 3)
 * 
 * СВЯЗАННЫЕ МОДУЛИ:
 * - api/serviceClusters.ts - типы ServiceCluster
 * - hooks/useServiceClusters.ts - загрузка кластеров
 * - pages/Services.tsx - страница услуг
 * 
 * КАК РАБОТАЕТ:
 * - Отображает цветные бейджи для каждого кластера, в котором находится услуга
 * - Клик по бейджу → перейти к кластеру (scroll к ClusterCard)
 * - Если услуга в нескольких кластерах — показываются все бейджи
 * 
 * КОМПОЗИЦИЯ ФУНКЦИЙ:
 *   ServiceClusterBadge({ clusters, serviceId, onClusterClick })
 *     └── Badge компоненты для каждого кластера
 * 
 * ИСПОЛЬЗОВАНИЕ ИЗ ДРУГИХ МОДУЛЕЙ:
 * - pages/Services.tsx: отображается на каждой карточке услуги
 */
import { Badge } from '@/components/ui';
import { ServiceCluster } from '@/api/service-clusters';
import { cn } from '@/utils/cn';
import { getClusterColor, getClusterIconGlyph } from './clusterVisuals';

interface ServiceClusterBadgeProps {
  clusters: ServiceCluster[];
  serviceId: string;
  onClusterClick?: (clusterId: string) => void;
  className?: string;
  compact?: boolean;
}

export function ServiceClusterBadge({
  clusters,
  serviceId: _serviceId,
  onClusterClick,
  className,
  compact = false,
}: ServiceClusterBadgeProps) {
  if (!clusters || clusters.length === 0) {
    return null;
  }

  if (compact) {
    return (
      <div className={cn('flex items-center gap-1 flex-wrap', className)}>
        {clusters.map((cluster) => (
          <Badge
            key={cluster.id}
            variant="secondary"
            className="text-xs cursor-pointer hover:opacity-80 transition-opacity"
            style={{
              backgroundColor: `${getClusterColor(cluster.color, clusters.indexOf(cluster))}20`,
              color: getClusterColor(cluster.color, clusters.indexOf(cluster)),
              borderColor: getClusterColor(cluster.color, clusters.indexOf(cluster)),
            }}
            onClick={() => onClusterClick?.(cluster.id)}
          >
            {getClusterIconGlyph(cluster.icon)} {cluster.name}
          </Badge>
        ))}
      </div>
    );
  }

  return (
    <div className={cn('flex items-center gap-2 flex-wrap', className)}>
      {clusters.map((cluster) => (
        <Badge
          key={cluster.id}
          variant="secondary"
          className="text-xs cursor-pointer hover:opacity-80 transition-opacity px-2 py-1"
          style={{
            backgroundColor: `${getClusterColor(cluster.color, clusters.indexOf(cluster))}20`,
            color: getClusterColor(cluster.color, clusters.indexOf(cluster)),
            borderColor: getClusterColor(cluster.color, clusters.indexOf(cluster)),
            borderWidth: '1px',
            borderStyle: 'solid',
          }}
          onClick={() => onClusterClick?.(cluster.id)}
          title={`Кластер: ${cluster.name}${cluster.description ? ` - ${cluster.description}` : ''}`}
        >
          <span className="mr-1">{getClusterIconGlyph(cluster.icon)}</span>
          {cluster.name}
        </Badge>
      ))}
    </div>
  );
}
